import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Languages } from 'lucide-react';

import { useI18n } from '@/shared/contexts/I18nProvider';
import { languageDetails, supportedLanguages } from '@/shared/i18n';

type LanguageSelectorProps = {
  menuAlign?: 'left' | 'right';
  menuPlacement?: 'bottom' | 'top';
};

const LanguageSelector = ({
  menuAlign = 'right',
  menuPlacement = 'bottom'
}: LanguageSelectorProps) => {
  const { content, language, setLanguage } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement | null>(null);
  const menuAlignmentClassName = menuAlign === 'left' ? 'left-0' : 'right-0';
  const menuPlacementClassName =
    menuPlacement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2';

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!selectorRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleLanguageSelect = (nextLanguage: typeof language) => {
    setLanguage(nextLanguage);
    setIsOpen(false);
  };

  return (
    <div ref={selectorRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setIsOpen(current => !current)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={content.language.selectorLabel}
        title={content.language.selectorLabel}
        className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-border-primary/70 bg-background-card/35 px-2.5 font-mono text-[0.7rem] font-semibold text-text-primary shadow-sm shadow-shadow backdrop-blur-md transition-colors hover:bg-background-hover/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2"
      >
        <Languages className="h-3.5 w-3.5" aria-hidden="true" />
        <span>{languageDetails[language].shortLabel}</span>
        <ChevronDown
          className={[
            'h-3 w-3 text-text-muted transition-transform duration-200',
            isOpen ? 'rotate-180' : ''
          ].join(' ')}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label={content.language.selectorLabel}
          className={[
            'absolute z-50 w-36 overflow-hidden rounded-md border border-border-primary bg-background-card/95 p-1 shadow-xl shadow-shadow backdrop-blur-xl',
            menuAlignmentClassName,
            menuPlacementClassName
          ].join(' ')}
        >
          {supportedLanguages.map(languageCode => {
            const isSelected = languageCode === language;

            return (
              <button
                key={languageCode}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => handleLanguageSelect(languageCode)}
                className={[
                  'flex h-8 w-full cursor-pointer items-center justify-between rounded px-2.5 font-mono text-xs transition-colors',
                  isSelected
                    ? 'bg-background-hover text-accent-primary'
                    : 'text-text-secondary hover:bg-background-hover/70 hover:text-text-primary'
                ].join(' ')}
              >
                <span>{content.language.names[languageCode]}</span>
                {isSelected && <Check className="h-3.5 w-3.5" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
